import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Bell,
  Calendar,
  CheckCircle,
  CheckSquare,
  ClipboardList,
  Clock,
  Columns3,
  Copy,
  ExternalLink,
  List,
  Loader2,
  Mail,
  Phone,
  Square,
  Trash2,
  User,
  X,
  XCircle,
  ArrowRight,
  FileText,
  MessageSquare,
  Settings2,
  Bot,
} from 'lucide-react';
import LeadWorkspaceLinks from '../../components/vendor/leads/LeadWorkspaceLinks';
import AskAgentModal from '../../components/vendor/workspace/AskAgentModal';
import LeadPipelineBoard from '../../components/vendor/leads/LeadPipelineBoard';
import { useVendorIntegrations } from '../../services/vendorIntegrationsService';
import ConsultationSettings from '../../components/vendor/ConsultationSettings';
import { PIPELINE_BOARD_STATUSES } from '../../utils/leadFormFields';
import { useToastContext } from '../../contexts/ToastContext';
import { useConfirm } from '../../contexts/ConfirmContext';
import {
  getConsultations,
  getConsultation,
  getConsultationReminders,
  getLeadCatalog,
  getLeadLossInsights,
  acceptConsultation,
  rejectConsultation,
  completeConsultation,
  deleteConsultation,
  updateLeadStatus,
  updateLeadSchedule,
  updateLeadChecklist,
  addLeadNote,
  logLeadEmail,
  logLeadCall,
  saveLeadDeal,
  completeConsultationReminder,
} from '../../services/consultationService';
import Button from '../../components/shared/Button';
import Input from '../../components/shared/Input';
import CustomSelect from '../../components/shared/CustomSelect';
import ModalShell from '../../components/shared/ModalShell';
import DatePicker from '../../components/client/DatePicker';
import TimePicker from '../../components/client/TimePicker';
import { VendorPageHeader, VendorGuideCallout } from '../../components/vendor/guidance';
import { VENDOR_PAGE_GUIDES } from '../../utils/vendorPageGuides';

const toDateInputValue = (d) => {
  if (!d) return '';
  const s = String(d);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const toTimeInputValue = (t) => {
  if (!t) return '';
  const parts = String(t).split(':');
  const h = String(parts[0] || '0').padStart(2, '0');
  const m = String(parts[1] || '00').padStart(2, '0');
  return `${h}:${m}`;
};

/** Drop questionnaire rows that duplicate the Contact card (email/phone). */
const filterClientRequestRows = (rows, lead) => {
  const contactEmail = String(
    lead?.client_display_email || lead?.client_email || lead?.client?.email || ''
  )
    .trim()
    .toLowerCase();
  const contactPhone = String(
    lead?.client_display_phone || lead?.client_phone || lead?.client?.phone || ''
  ).replace(/\D/g, '');

  return (rows || []).filter((row) => {
    if (row?.value == null || String(row.value).trim() === '') return false;
    const key = String(row.key || '').toLowerCase();
    const label = String(row.label || '').trim().toLowerCase();
    if (
      /^(guest_)?(e[-_]?mail|phone|telephone|mobile|contact)$/.test(key) ||
      /^(e[-_]?mail|phone|telephone|mobile|contact)$/.test(label)
    ) {
      return false;
    }
    const val = String(row.value).trim().toLowerCase();
    if (contactEmail && val === contactEmail) return false;
    const digits = val.replace(/\D/g, '');
    if (contactPhone && digits && digits === contactPhone) return false;
    return true;
  });
};

const meetingLinkAlreadySent = (lead) => Boolean(lead?.meet_link_sent_at);

const STATUS_TABS = [
  { key: 'pending', label: 'Pending' },
  { key: 'meeting_scheduled', label: 'Meeting Scheduled' },
  { key: 'meeting_done', label: 'Meeting Done' },
  { key: 'booked', label: 'Booked' },
  { key: 'lead_not_responsive', label: 'Not Responsive' },
  { key: 'lead_cancelled', label: 'Cancelled' },
  { key: 'rejected', label: 'Rejected' },
  { key: '', label: 'All' },
];

const REMINDER_FILTERS = [
  { key: 'due_today', label: 'Due today' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'upcoming', label: 'Upcoming' },
  { key: 'all', label: 'All open' },
  { key: 'completed', label: 'Completed' },
];

const STATUS_BADGE = {
  pending: 'bg-amber-50 text-amber-800 border-amber-200',
  meeting_scheduled: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  meeting_done: 'bg-surface-muted text-ink border-border',
  booked: 'bg-green-50 text-green-800 border-green-200',
  lead_not_responsive: 'bg-orange-50 text-orange-800 border-orange-200',
  lead_cancelled: 'bg-red-50 text-red-700 border-red-200',
  rejected: 'bg-rose-50 text-rose-700 border-rose-200',
  accepted: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  completed: 'bg-surface-muted text-ink border-border',
};

const DEAL_FIELDS = [
  { key: 'package', label: 'Package selected' },
  { key: 'services', label: 'Services included' },
  { key: 'pricing', label: 'Pricing' },
  { key: 'deliverables', label: 'Important deliverables' },
  { key: 'special_arrangements', label: 'Special arrangements' },
  { key: 'payment_terms', label: 'Payment / deposit' },
  { key: 'other_terms', label: 'Other terms' },
];

const tagsToInput = (raw) => {
  if (Array.isArray(raw)) return raw.join(', ');
  if (typeof raw === 'string') return raw;
  return '';
};

const normalizeMeetingLink = (value) => {
  const trimmed = String(value || '').trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed.replace(/^\/+/, '')}`;
};

const getApiErrorMessage = (err, fallback) => {
  const data = err?.response?.data;
  if (data?.message) return data.message;
  const errors = data?.errors;
  if (errors && typeof errors === 'object') {
    const first = Object.values(errors).flat().find(Boolean);
    if (first) return String(first);
  }
  return fallback;
};

const clientNameOf = (c) =>
  c?.client?.name || c?.client_name || c?.client_display_name || 'Client';

const formatDate = (d) =>
  d
    ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : '—';

const formatDateTime = (d) =>
  d
    ? new Date(d).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : '—';

const formatTime = (t) => {
  if (!t) return '—';
  const parts = String(t).split(':');
  const h = parseInt(parts[0] || 0, 10);
  const m = parts[1] || '00';
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h > 12 ? h - 12 : h === 0 ? 12 : h;
  return `${h12}:${m} ${ampm}`;
};

const StatusBadge = ({ status, label }) => (
  <span
    className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium border ${
      STATUS_BADGE[status] || 'bg-gray-100 text-gray-700 border-gray-200'
    }`}
  >
    {label || status?.replace(/_/g, ' ')}
  </span>
);

const normalizeStatusParam = (raw) => {
  if (raw == null || raw === 'all') return '';
  if (raw === 'accepted') return 'meeting_scheduled';
  if (raw === 'completed') return 'meeting_done';
  return raw;
};

const REMINDER_FILTER_KEYS = REMINDER_FILTERS.map((f) => f.key);

const VendorConsultations = () => {
  const { success, error: showError } = useToastContext();
  const { confirm } = useConfirm();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const statusFromUrl = normalizeStatusParam(searchParams.get('status') ?? 'pending');
  const reminderFromUrl = (() => {
    const r = searchParams.get('reminders') || 'due_today';
    return REMINDER_FILTER_KEYS.includes(r) ? r : 'due_today';
  })();
  const view = searchParams.get('view') === 'list' ? 'list' : 'board';

  const [statusFilter, setStatusFilter] = useState(statusFromUrl);
  const [reminderFilter, setReminderFilter] = useState(reminderFromUrl);
  const [selectedLead, setSelectedLead] = useState(null);
  const selectedLeadRef = useRef(null);
  const [showAskAgent, setShowAskAgent] = useState(false);
  const [showFormSettings, setShowFormSettings] = useState(
    () => searchParams.get('settings') === '1' || searchParams.get('settings') === 'form'
  );
  const { data: integRes } = useVendorIntegrations({ enabled: Boolean(selectedLead?.id) });
  const canAgents = Boolean(integRes?.data?.capabilities?.can_agents);

  // Keep filters in the URL so reload / share restores the selected tabs.
  const syncFiltersToUrl = (nextStatus, nextReminder, nextView = view) => {
    const next = new URLSearchParams(searchParams);
    if (nextStatus) next.set('status', nextStatus);
    else next.set('status', 'all');
    next.set('reminders', nextReminder || 'due_today');
    if (nextView === 'list') next.set('view', 'list');
    else next.delete('view');
    setSearchParams(next, { replace: true });
  };

  const setView = (nextView) => {
    syncFiltersToUrl(statusFilter, reminderFilter, nextView);
  };

  useEffect(() => {
    const s = normalizeStatusParam(searchParams.get('status') ?? 'pending');
    const r = searchParams.get('reminders') || 'due_today';
    setStatusFilter(s);
    setReminderFilter(REMINDER_FILTER_KEYS.includes(r) ? r : 'due_today');
  }, [searchParams]);

  useEffect(() => {
    selectedLeadRef.current = selectedLead;
  }, [selectedLead]);

  const selectStatusFilter = (key) => {
    setStatusFilter(key);
    syncFiltersToUrl(key, reminderFilter);
  };

  const selectReminderFilter = (key) => {
    setReminderFilter(key);
    syncFiltersToUrl(statusFilter, key);
  };

  const [showAcceptModal, setShowAcceptModal] = useState(null);
  const [acceptedMeetingLink, setAcceptedMeetingLink] = useState(null);
  const [meetingLink, setMeetingLink] = useState('');
  const [meetingLinkError, setMeetingLinkError] = useState('');
  const [vendorNotes, setVendorNotes] = useState('');

  const [statusDraft, setStatusDraft] = useState('');
  const [closeReason, setCloseReason] = useState('');
  const [closeNotes, setCloseNotes] = useState('');
  const [noteDraft, setNoteDraft] = useState('');
  const [callOutcome, setCallOutcome] = useState('');
  const [callNotes, setCallNotes] = useState('');
  const [dealDraft, setDealDraft] = useState({});
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [scheduleLink, setScheduleLink] = useState('');
  const [calendarTitleDraft, setCalendarTitleDraft] = useState('');
  const [pendingCalendarAdd, setPendingCalendarAdd] = useState(null);
  const [showInsights, setShowInsights] = useState(false);

  const params = statusFilter ? { status: statusFilter } : {};

  const { data: catalogData } = useQuery({
    queryKey: ['leadCatalog'],
    queryFn: getLeadCatalog,
    staleTime: 10 * 60 * 1000,
  });
  const catalog = catalogData?.data || {};
  const statuses = catalog.statuses || {};
  const callOutcomes = catalog.call_outcomes || {};

  const { data: consultationsData, isLoading } = useQuery({
    queryKey: ['vendorConsultations', params],
    queryFn: () => getConsultations(params),
    enabled: view === 'list',
    staleTime: 8_000,
    refetchInterval: 15_000,
    refetchIntervalInBackground: false,
  });
  const consultations = consultationsData?.data?.data || consultationsData?.data || [];

  const { data: boardData, isLoading: boardLoading } = useQuery({
    queryKey: ['vendorConsultationsBoard'],
    queryFn: () => getConsultations({ per_page: 100 }),
    enabled: view === 'board',
    staleTime: 8_000,
    refetchInterval: 15_000,
    refetchIntervalInBackground: false,
  });
  const boardLeads = boardData?.data?.data || boardData?.data || [];

  const boardColumns = useMemo(() => {
    return PIPELINE_BOARD_STATUSES.map((status) => {
      const meta = statuses[status] || {};
      const items = (Array.isArray(boardLeads) ? boardLeads : []).filter(
        (l) => l.status === status
      );
      return {
        status,
        label: meta.label || status.replace(/_/g, ' '),
        color: meta.color || 'gray',
        is_terminal: meta.is_terminal,
        count: items.length,
        leads: items.map((l) => {
          const metaKeys = meta.checklist || [];
          const stored = l.action_checklist || {};
          const labels = meta.checklist_labels || {};
          const progressItems = metaKeys.map((key) => ({
            key,
            label: labels[key] || key,
            done: Boolean(stored[key]),
          }));
          return {
            ...l,
            guest_name: l.client_display_name || l.client?.name || l.client_name,
            guest_email: l.client?.email || l.client_email,
            guest_phone: l.client?.phone || l.client_phone,
            checklist_progress: l.checklist_progress || {
              items: progressItems,
              completed: progressItems.filter((i) => i.done).length,
              total: progressItems.length,
            },
            primary_invoice_id: l.primary_invoice_id || l.links?.invoice_id || null,
            primary_contract_id: l.primary_contract_id || l.links?.contract_id || null,
            links: {
              booking_id: l.booking_id || l.links?.booking_id || null,
              invoice_id: l.primary_invoice_id || l.links?.invoice_id || null,
              contract_id: l.primary_contract_id || l.links?.contract_id || null,
            },
          };
        }),
      };
    });
  }, [boardLeads, statuses]);

  const { data: remindersData, isLoading: remindersLoading } = useQuery({
    queryKey: ['consultationReminders', reminderFilter],
    queryFn: () => getConsultationReminders({ filter: reminderFilter }),
    staleTime: 8_000,
    refetchInterval: 20_000,
    refetchIntervalInBackground: false,
  });
  const reminders = remindersData?.data || [];

  const { data: insightsData } = useQuery({
    queryKey: ['leadLossInsights'],
    queryFn: getLeadLossInsights,
    enabled: showInsights,
  });
  const insights = insightsData?.data;

  const invalidateAll = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['vendorConsultations'] }),
      queryClient.invalidateQueries({ queryKey: ['vendorConsultationsBoard'] }),
      queryClient.invalidateQueries({ queryKey: ['consultationReminders'] }),
      queryClient.invalidateQueries({ queryKey: ['leadLossInsights'] }),
      queryClient.invalidateQueries({ queryKey: ['consultationCalendarLeads'] }),
      queryClient.invalidateQueries({ queryKey: ['pendingConsultationLeadCount'] }),
    ]);
  };

  const syncSelected = (lead, { resetDrafts = true } = {}) => {
    if (!lead) return;
    selectedLeadRef.current = lead;
    setSelectedLead(lead);
    if (resetDrafts) {
      setStatusDraft(lead.status);
      const deal = lead.deal_summary || {};
      const eventDate =
        lead.event_date ||
        deal.event_date ||
        '';
      setDealDraft({
        ...deal,
        event_date: eventDate ? String(eventDate).slice(0, 10) : '',
        tags: tagsToInput(deal.tags),
      });
      setCloseReason(lead.closed_reason || '');
      setCloseNotes(lead.closed_notes || '');
      setScheduleDate(toDateInputValue(lead.scheduled_date));
      setScheduleTime(toTimeInputValue(lead.scheduled_time));
      setScheduleLink(lead.google_meet_link || '');
      setCalendarTitleDraft(lead.calendar_title || '');
    }
  };

  const patchLeadInListCaches = (lead) => {
    if (!lead?.id) return;
    queryClient.setQueriesData({ queryKey: ['vendorConsultations'] }, (old) => {
      if (!old) return old;
      const page = old?.data?.data || old?.data;
      if (!Array.isArray(page)) return old;
      const nextPage = page.map((row) => (row.id === lead.id ? { ...row, ...lead } : row));
      if (old?.data?.data) {
        return { ...old, data: { ...old.data, data: nextPage } };
      }
      if (Array.isArray(old?.data)) {
        return { ...old, data: nextPage };
      }
      return old;
    });
  };

  /** Patch lead into list caches + open modal so UI shows new data immediately. */
  const applyLeadUpdate = (lead, { resetDrafts = true } = {}) => {
    if (!lead?.id) return;
    syncSelected(lead, { resetDrafts });
    patchLeadInListCaches(lead);
  };

  /** After a mutation, apply payload then refetch detail so reminders/activities stay fresh. */
  const commitLeadUpdate = async (payload, options = {}) => {
    const lead = payload?.id ? payload : null;
    if (lead) applyLeadUpdate(lead, options);
    await invalidateAll();
    const openId = selectedLeadRef.current?.id || lead?.id;
    if (!openId) return;
    try {
      const res = await getConsultation(openId);
      if (res?.data) applyLeadUpdate(res.data, options);
    } catch {
      // Keep optimistic / mutation payload if detail refetch fails.
    }
  };

  const acceptMutation = useMutation({
    mutationFn: ({ id, data }) => acceptConsultation(id, data),
    onSuccess: async (res) => {
      const link = res?.data?.google_meet_link;
      if (link) {
        setAcceptedMeetingLink(link);
        if (res?.already_sent) {
          success(null, 'Already emailed — copy the link to share again.');
        }
      } else {
        success(null, res?.message || 'Meeting scheduled.');
        closeAcceptModal();
      }
      await commitLeadUpdate(res?.data);
    },
    onError: (err) => {
      const message = getApiErrorMessage(err, 'Failed to schedule meeting');
      const linkError = err?.response?.data?.errors?.google_meet_link?.[0];
      if (linkError) setMeetingLinkError(linkError);
      showError('Could not schedule meeting', message);
    },
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, data }) => rejectConsultation(id, data),
    onSuccess: async () => {
      success(null, 'Lead rejected');
      selectedLeadRef.current = null;
      setSelectedLead(null);
      await invalidateAll();
    },
    onError: (err) => showError(null, getApiErrorMessage(err, 'Failed to reject')),
  });

  const completeMutation = useMutation({
    mutationFn: (id) => completeConsultation(id),
    onSuccess: async (res) => {
      success(null, 'Meeting done — 14-day follow-up reminder scheduled');
      await commitLeadUpdate(res?.data);
    },
    onError: (err) => showError(null, getApiErrorMessage(err, 'Failed to mark done')),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => deleteConsultation(id),
    onSuccess: async () => {
      success(null, 'Lead deleted');
      selectedLeadRef.current = null;
      setSelectedLead(null);
      if (searchParams.get('lead')) {
        const next = new URLSearchParams(searchParams);
        next.delete('lead');
        setSearchParams(next, { replace: true });
      }
      await invalidateAll();
    },
    onError: (err) => showError(null, getApiErrorMessage(err, 'Failed to delete lead')),
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, data }) => updateLeadStatus(id, data),
    onSuccess: async (res) => {
      success(null, 'Lead status updated');
      await commitLeadUpdate(res?.data);
    },
    onError: (err) => showError(null, getApiErrorMessage(err, 'Failed to update status')),
  });

  const scheduleMutation = useMutation({
    mutationFn: ({ id, data }) => updateLeadSchedule(id, data),
    onSuccess: async (res) => {
      success(null, 'Consultation date updated');
      await commitLeadUpdate(res?.data);
    },
    onError: (err) => showError(null, getApiErrorMessage(err, 'Failed to update schedule')),
  });

  const checklistMutation = useMutation({
    mutationFn: ({ id, checklist, calendar_title }) =>
      updateLeadChecklist(
        id,
        checklist,
        calendar_title != null ? { calendar_title } : {}
      ),
    onMutate: async ({ id, checklist, snapshot }) => {
      await queryClient.cancelQueries({ queryKey: ['vendorConsultations'] });
      const previous = snapshot || selectedLeadRef.current;
      if (previous?.id === id) {
        const labels = previous.status_meta?.checklist_labels || {};
        const keys =
          previous.status_meta?.checklist ||
          previous.checklist_progress?.items?.map((i) => i.key) ||
          Object.keys(checklist);
        const items = keys.map((key) => ({
          key,
          label: labels[key] || previous.checklist_progress?.items?.find((i) => i.key === key)?.label || key,
          done: Boolean(checklist[key]),
        }));
        const completed = items.filter((i) => i.done).length;
        const optimistic = {
          ...previous,
          action_checklist: checklist,
          checklist_progress: { items, completed, total: items.length },
        };
        applyLeadUpdate(optimistic, { resetDrafts: false });
      }
      return { previous };
    },
    onSuccess: async (res, vars) => {
      const serverLead = res?.data;
      if (serverLead) {
        // Keep the toggled item checked even if a race returns a stale progress map.
        let nextLead = serverLead;
        if (vars?.toggledKey && typeof vars.checking === 'boolean') {
          const checklist = {
            ...(serverLead.action_checklist || {}),
            [vars.toggledKey]: vars.checking,
          };
          const items = (serverLead.checklist_progress?.items || []).map((item) =>
            item.key === vars.toggledKey ? { ...item, done: vars.checking } : item
          );
          const completed = items.filter((i) => i.done).length;
          nextLead = {
            ...serverLead,
            action_checklist: checklist,
            checklist_progress: {
              ...(serverLead.checklist_progress || {}),
              items,
              completed,
              total: items.length || serverLead.checklist_progress?.total || 0,
            },
            ...(vars.calendar_title != null ? { calendar_title: vars.calendar_title } : {}),
          };
        }
        applyLeadUpdate(nextLead, { resetDrafts: false });
      }
      const wasOnCalendar = Boolean(vars?.snapshot?.action_checklist?.added_to_calendar);
      if (vars?.toggledKey === 'added_to_calendar' && vars?.checking && !wasOnCalendar) {
        success(null, 'Lead added to your calendar');
      } else if (
        vars?.toggledKey === 'added_to_calendar' &&
        vars?.checking &&
        wasOnCalendar &&
        vars?.calendar_title
      ) {
        success(null, 'Calendar title updated');
      } else if (vars?.toggledKey === 'meeting_link_sent' && vars?.checking) {
        success(null, 'Meeting link sent to client');
      } else if (vars?.toggledKey === 'added_to_calendar' && !vars?.checking) {
        success(null, 'Lead removed from calendar');
      }
      await invalidateAll();
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.previous) {
        syncSelected(ctx.previous, { resetDrafts: false });
        patchLeadInListCaches(ctx.previous);
      }
      showError(null, getApiErrorMessage(err, 'Failed to update checklist'));
    },
  });

  const noteMutation = useMutation({
    mutationFn: ({ id, note }) => addLeadNote(id, note),
    onSuccess: async (res) => {
      success(null, 'Note saved');
      setNoteDraft('');
      await commitLeadUpdate(res?.data, { resetDrafts: false });
    },
    onError: (err) => showError(null, getApiErrorMessage(err, 'Failed to add note')),
  });

  const emailMutation = useMutation({
    mutationFn: (id) => logLeadEmail(id),
    onSuccess: async (res) => {
      success(null, 'Email logged — call reminder in 24h if no reply');
      await commitLeadUpdate(res?.data, { resetDrafts: false });
    },
    onError: (err) => showError(null, getApiErrorMessage(err, 'Failed to log email')),
  });

  const callMutation = useMutation({
    mutationFn: ({ id, data }) => logLeadCall(id, data),
    onSuccess: async (res) => {
      success(null, 'Call outcome recorded');
      setCallOutcome('');
      setCallNotes('');
      await commitLeadUpdate(res?.data, { resetDrafts: false });
    },
    onError: (err) => showError(null, getApiErrorMessage(err, 'Failed to log call')),
  });

  const dealMutation = useMutation({
    mutationFn: ({ id, deal }) => saveLeadDeal(id, deal),
    onSuccess: async (res) => {
      success(null, 'Deal summary saved');
      await commitLeadUpdate(res?.data);
    },
    onError: (err) => showError(null, getApiErrorMessage(err, 'Failed to save deal')),
  });

  const reminderMutation = useMutation({
    mutationFn: ({ reminderId, data }) => completeConsultationReminder(reminderId, data),
    onSuccess: async (res) => {
      success(null, 'Reminder updated');
      await commitLeadUpdate(res?.data?.lead || res?.data, { resetDrafts: false });
    },
    onError: (err) => showError(null, getApiErrorMessage(err, 'Failed to update reminder')),
  });

  const openLead = async (lead) => {
    syncSelected(lead);
    setNoteDraft('');
    setCallOutcome('');
    setCallNotes('');
    try {
      const res = await getConsultation(lead.id);
      if (res?.data) applyLeadUpdate(res.data);
    } catch {
      // Keep list payload if detail fetch fails.
    }
  };

  // Deep-link from calendar: /vendor/consultations?lead=<id>
  useEffect(() => {
    const leadId = searchParams.get('lead');
    if (!leadId) return;
    if (selectedLeadRef.current?.id === leadId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await getConsultation(leadId);
        if (!cancelled && res?.data) {
          applyLeadUpdate(res.data);
        }
      } catch {
        if (!cancelled) {
          showError(null, 'Could not open that lead');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- open once when lead param appears
  }, [searchParams]);

  const closeAcceptModal = () => {
    setShowAcceptModal(null);
    setAcceptedMeetingLink(null);
    setMeetingLink('');
    setMeetingLinkError('');
    setVendorNotes('');
  };

  const handleAccept = (consultation) => {
    const normalizedLink = normalizeMeetingLink(meetingLink);
    if (!normalizedLink) {
      setMeetingLinkError('Please enter a meeting link');
      return;
    }
    try {
      // eslint-disable-next-line no-new
      new URL(normalizedLink);
    } catch {
      setMeetingLinkError('Enter a valid https:// URL');
      return;
    }
    setMeetingLinkError('');
    acceptMutation.mutate({
      id: consultation.id,
      data: { google_meet_link: normalizedLink, vendor_notes: vendorNotes || null },
    });
  };

  const handleStatusChange = () => {
    if (!selectedLead || !statusDraft || statusDraft === selectedLead.status) return;
    const meta = statuses[statusDraft] || {};
    if (meta.requires_close_reason && !closeReason) {
      showError(null, 'Select a reason before closing this lead');
      return;
    }
    statusMutation.mutate({
      id: selectedLead.id,
      data: {
        status: statusDraft,
        closed_reason: closeReason || undefined,
        closed_notes: closeNotes || undefined,
      },
    });
  };

  const runChecklistClientAction = (lead, key, checking) => {
    if (!checking) return;
    const email =
      lead.client_display_email || lead.client_email || lead.client?.email;
    const name = clientNameOf(lead);
    const when = `${formatDate(lead.scheduled_date)} ${formatTime(lead.scheduled_time)}`.trim();
    const meet = lead.google_meet_link || '';

    if (key === 'meeting_link_sent' && meet) {
      // Platform email runs once via API; after that (or when already sent) just copy.
      if (meetingLinkAlreadySent(lead)) {
        navigator.clipboard?.writeText(meet).then(
          () => success(null, 'Link copied — share from your own email if needed.'),
          () => success(null, 'Meeting link already sent. Copy it manually to re-share.')
        );
        return;
      }
      return;
    }

    if (key === 'day_before_reminder_sent' && email) {
      const subject = encodeURIComponent(`Reminder: consultation tomorrow — ${name}`);
      const body = encodeURIComponent(
        `Hi ${name},\n\nFriendly reminder about our consultation:\n${when}${
          meet ? `\n\nJoin: ${meet}` : ''
        }\n\nLooking forward to speaking with you.`
      );
      window.open(`mailto:${email}?subject=${subject}&body=${body}`, '_blank');
      return;
    }

    if (key === 'joined_early' && email) {
      const subject = encodeURIComponent(`I'm ready for our consultation — ${name}`);
      const body = encodeURIComponent(
        `Hi ${name},\n\nI've joined a few minutes early and I'm ready whenever you are.${
          meet ? `\n\nMeeting link: ${meet}` : ''
        }`
      );
      window.open(`mailto:${email}?subject=${subject}&body=${body}`, '_blank');
      return;
    }

    if (key === 'follow_up_message_sent' && email) {
      const subject = encodeURIComponent(`Following up after our consultation — ${name}`);
      const body = encodeURIComponent(
        `Hi ${name},\n\nThank you for meeting with me. Here is a quick summary of next steps and the agreed package details.\n\nPlease reply if you have any questions — I'm happy to help you move forward.`
      );
      window.open(`mailto:${email}?subject=${subject}&body=${body}`, '_blank');
      return;
    }

    if (key === 'contacted_client' && email) {
      const subject = encodeURIComponent(`Regarding your consultation request — ${name}`);
      window.open(`mailto:${email}?subject=${subject}`, '_blank');
    }
  };

  const buildChecklistMap = (lead, key, checking) => {
    const current = {};
    const progressItems = lead.checklist_progress?.items || [];
    const keys = lead.status_meta?.checklist?.length
      ? lead.status_meta.checklist
      : progressItems.map((i) => i.key);
    keys.forEach((k) => {
      const fromProgress = progressItems.find((i) => i.key === k)?.done;
      const fromStored = lead.action_checklist?.[k];
      current[k] = Boolean(fromProgress ?? fromStored ?? false);
    });
    if (key) current[key] = checking;
    return { current, keys, progressItems };
  };

  const applyChecklistMutation = (lead, key, checking, checklist, extras = {}) => {
    const labels = lead.status_meta?.checklist_labels || {};
    const progressItems = lead.checklist_progress?.items || [];
    const keys = lead.status_meta?.checklist?.length
      ? lead.status_meta.checklist
      : progressItems.map((i) => i.key);
    const snapshot = { ...lead };
    const items = keys.map((k) => ({
      key: k,
      label: labels[k] || progressItems.find((i) => i.key === k)?.label || k,
      done: Boolean(checklist[k]),
    }));
    const optimistic = {
      ...lead,
      action_checklist: checklist,
      checklist_progress: {
        items,
        completed: items.filter((i) => i.done).length,
        total: items.length,
      },
      ...(extras.calendar_title != null ? { calendar_title: extras.calendar_title } : {}),
    };
    selectedLeadRef.current = optimistic;
    applyLeadUpdate(optimistic, { resetDrafts: false });

    if (key !== 'added_to_calendar' || !checking) {
      runChecklistClientAction(lead, key, checking);
    }
    checklistMutation.mutate({
      id: lead.id,
      checklist,
      toggledKey: key,
      checking,
      snapshot,
      ...extras,
    });
  };

  const toggleChecklistItem = (key, done) => {
    const lead = selectedLeadRef.current;
    if (!lead || checklistMutation.isPending) return;
    const checking = !done;

    if (checking && key === 'meeting_link_sent' && !lead.google_meet_link) {
      showError(null, 'Add a meeting link before marking this complete');
      return;
    }
    if (checking && key === 'added_to_calendar' && !lead.scheduled_date) {
      showError(null, 'Set a consultation date before adding to calendar');
      return;
    }
    if (checking && key === 'meeting_confirmed' && !lead.scheduled_date) {
      showError(null, 'Schedule a date and time before confirming the meeting');
      return;
    }

    const { current } = buildChecklistMap(lead, key, checking);

    // Ask for a calendar title before pinning the lead.
    if (checking && key === 'added_to_calendar') {
      const defaultTitle =
        lead.calendar_title || `Consultation — ${clientNameOf(lead)}`;
      setCalendarTitleDraft(defaultTitle);
      setPendingCalendarAdd({ leadId: lead.id, checklist: current, lead });
      return;
    }

    applyChecklistMutation(lead, key, checking, current);
  };

  const confirmAddToCalendar = () => {
    if (!pendingCalendarAdd) return;
    const title = calendarTitleDraft.trim();
    if (!title) {
      showError(null, 'Enter a title for the calendar event');
      return;
    }
    if (title.length > 180) {
      showError(null, 'Title must be 180 characters or fewer');
      return;
    }
    const { lead, checklist } = pendingCalendarAdd;
    // Ensure the pin flag is set even if the stored map was stale.
    const nextChecklist = { ...checklist, added_to_calendar: true };
    setPendingCalendarAdd(null);
    // Defer so unmounting any overlay cannot steal this click and close the lead modal.
    window.setTimeout(() => {
      applyChecklistMutation(lead, 'added_to_calendar', true, nextChecklist, {
        calendar_title: title,
      });
    }, 0);
  };

  const statusMeta = selectedLead?.status_meta || statuses[selectedLead?.status] || {};
  const checklistItems = selectedLead?.checklist_progress?.items || [];
  const draftStatusMeta = statuses[statusDraft] || {};
  const needsCloseReason = Boolean(draftStatusMeta.requires_close_reason);

  const actionPlan = useMemo(
    () => statusMeta.action_plan || [],
    [statusMeta.action_plan]
  );

  const emptyCopy = statusFilter
    ? `No ${statuses[statusFilter]?.label || statusFilter.replace(/_/g, ' ')} leads right now.`
    : 'When clients request a consultation, they appear here as leads.';

  return (
    <div className="space-y-5">
      <VendorPageHeader
        title="Lead management"
        subtitle="Track consultation leads from request through meeting, follow-up, and booking — board or list."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setShowFormSettings((v) => !v)}
              className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium ${
                showFormSettings
                  ? 'border-ink bg-ink text-white'
                  : 'border-border bg-surface text-ink hover:bg-surface-muted'
              }`}
            >
              <Settings2 className="h-3.5 w-3.5" />
              {showFormSettings ? 'Hide form' : 'Consultation form'}
            </button>
            <div className="inline-flex rounded-lg border border-border bg-surface p-0.5">
              <button
                type="button"
                onClick={() => setView('board')}
                className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium ${
                  view === 'board' ? 'bg-ink text-white' : 'text-ink-secondary hover:bg-surface-muted'
                }`}
              >
                <Columns3 className="h-3.5 w-3.5" />
                Board
              </button>
              <button
                type="button"
                onClick={() => setView('list')}
                className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium ${
                  view === 'list' ? 'bg-ink text-white' : 'text-ink-secondary hover:bg-surface-muted'
                }`}
              >
                <List className="h-3.5 w-3.5" />
                List
              </button>
            </div>
          </div>
        }
      />

      <VendorGuideCallout
        title={VENDOR_PAGE_GUIDES.consultations.title}
        steps={VENDOR_PAGE_GUIDES.consultations.steps}
        icon={ClipboardList}
      />

      {showFormSettings && (
        <section className="overflow-hidden rounded-lg border border-border bg-surface shadow-sm">
          <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3 sm:px-5">
            <div>
              <h2 className="text-sm font-semibold text-ink">Consultation form</h2>
              <p className="mt-0.5 text-xs text-ink-tertiary">
                Enable booking on your services and build the questionnaire clients answer.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowFormSettings(false)}
              className="rounded-md border border-border bg-surface px-2.5 py-1 text-xs font-medium text-ink-secondary hover:bg-surface-muted"
            >
              Close
            </button>
          </div>
          <div className="p-4 sm:p-5">
            <ConsultationSettings />
          </div>
        </section>
      )}

      {/* Today's Follow-Ups */}
      <div className="product-panel overflow-hidden">
        <div className="border-b border-border px-4 py-3 sm:px-5 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Bell className="w-4 h-4 text-ink" />
            <h2 className="text-sm font-semibold text-ink">Today&apos;s follow-ups</h2>
            {reminders.length > 0 && reminderFilter === 'due_today' && (
              <span className="text-xs font-medium text-ink-secondary bg-surface-muted border border-border px-2 py-0.5 rounded-full">
                {reminders.length} action{reminders.length === 1 ? '' : 's'}
              </span>
            )}
          </div>
          <div className="inline-flex flex-wrap rounded-xl bg-gray-100 p-1 border border-border">
            {REMINDER_FILTERS.map((f) => (
              <button
                key={f.key}
                type="button"
                onClick={() => selectReminderFilter(f.key)}
                className={`px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  reminderFilter === f.key
                    ? 'bg-surface text-ink shadow-sm'
                    : 'text-ink-secondary hover:text-gray-900'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
        <div className="p-4 sm:p-5">
          {remindersLoading ? (
            <div className="py-8 text-center">
              <Loader2 className="w-6 h-6 animate-spin text-ink mx-auto" />
            </div>
          ) : reminders.length === 0 ? (
            <p className="text-sm text-ink-tertiary text-center py-6">
              No reminders in this view. Follow-ups appear after a meeting is marked done.
            </p>
          ) : (
            <div className="space-y-3">
              {reminders.map((r) => {
                const lead = r.consultation;
                const name = clientNameOf(lead);
                return (
                  <div
                    key={r.id}
                    className="product-surface-muted p-3 sm:p-4 flex flex-col sm:flex-row sm:items-start gap-3"
                  >
                    <div className="flex-1 min-w-0 space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-sm font-semibold text-ink">{r.title}</p>
                        <StatusBadge status={r.status} label={r.status} />
                      </div>
                      <p className="text-xs text-ink-secondary leading-relaxed">{r.body}</p>
                      <p className="text-[11px] text-ink-tertiary">
                        Due {formatDateTime(r.due_at)}
                        {lead?.status_meta?.label ? ` · Lead: ${lead.status_meta.label}` : ''}
                      </p>
                      {Array.isArray(r.recommended_checklist) && r.recommended_checklist.length > 0 && (
                        <ul className="mt-2 space-y-0.5 text-xs text-ink-secondary">
                          {r.recommended_checklist.slice(0, 4).map((item) => (
                            <li key={item} className="flex gap-1.5">
                              <span className="text-ink-tertiary">•</span>
                              {item}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2 shrink-0">
                      {r.recommended_action === 'send_email' && (
                        <Button
                          size="sm"
                          variant="primary"
                          className="!rounded-lg"
                          loading={reminderMutation.isPending}
                          onClick={() => {
                            const email =
                              lead?.client?.email ||
                              lead?.client_email ||
                              lead?.client_display_email;
                            if (email) {
                              const subject = encodeURIComponent(
                                `Following up on your consultation — ${name}`
                              );
                              window.open(`mailto:${email}?subject=${subject}`, '_blank');
                            }
                            reminderMutation.mutate({
                              reminderId: r.id,
                              data: { action: 'send_email' },
                            });
                          }}
                        >
                          <Mail className="w-3.5 h-3.5 mr-1" />
                          Send email
                        </Button>
                      )}
                      {r.recommended_action === 'call_client' && (
                        <Button
                          size="sm"
                          variant="primary"
                          className="!rounded-lg"
                          onClick={() => {
                            const phone =
                              lead?.client?.phone ||
                              lead?.client_phone ||
                              lead?.client_display_phone;
                            if (phone) window.open(`tel:${phone}`);
                            if (lead) openLead(lead);
                          }}
                        >
                          <Phone className="w-3.5 h-3.5 mr-1" />
                          Call client
                        </Button>
                      )}
                      {lead && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="!rounded-lg"
                          onClick={() => openLead(lead)}
                        >
                          Open lead
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        className="!rounded-lg text-ink-tertiary"
                        onClick={() =>
                          reminderMutation.mutate({
                            reminderId: r.id,
                            data: { action: 'dismiss' },
                          })
                        }
                      >
                        Dismiss
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Pipeline — board (default) or list */}
      {view === 'board' ? (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm font-semibold text-ink">Lead board</p>
            <button
              type="button"
              onClick={() => setShowInsights((v) => !v)}
              className="text-xs font-medium text-ink-secondary hover:text-ink underline-offset-2 hover:underline"
            >
              {showInsights ? 'Hide loss insights' : 'Why are you losing leads?'}
            </button>
          </div>
          {showInsights && (
            <div className="product-panel p-4 sm:p-5">
              {!insights || !insights.reasons?.length ? (
                <p className="text-sm text-ink-tertiary">
                  No closed-lead reasons yet. When you mark leads Not Responsive or Cancelled, reasons
                  aggregate here.
                </p>
              ) : (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-ink">
                    Loss analysis · {insights.total_closed} closed lead
                    {insights.total_closed === 1 ? '' : 's'}
                  </p>
                  {insights.reasons.map((r) => (
                    <div key={r.reason} className="flex items-center gap-3 text-sm">
                      <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden">
                        <div
                          className="h-full bg-ink/80 rounded-full"
                          style={{ width: `${Math.max(r.percent, 4)}%` }}
                        />
                      </div>
                      <span className="w-28 text-ink-secondary truncate">{r.label}</span>
                      <span className="w-10 text-right font-medium text-ink">{r.percent}%</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {boardLoading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="h-7 w-7 animate-spin text-ink" />
            </div>
          ) : (
            <LeadPipelineBoard
              columns={boardColumns}
              isMoving={statusMutation.isPending}
              onOpenLead={openLead}
              onMoveStatus={(id, status) =>
                statusMutation.mutate({ id, data: { status } })
              }
            />
          )}
        </div>
      ) : null}

      {view === 'list' ? (
      <div className="product-panel overflow-hidden">
        <div className="border-b border-border px-4 py-3 sm:px-5 flex flex-wrap items-center justify-between gap-3">
          <div
            className="inline-flex flex-wrap rounded-xl bg-gray-100 p-1 border border-border"
            role="tablist"
            aria-label="Lead status"
          >
            {STATUS_TABS.map((tab) => {
              const isActive = statusFilter === tab.key;
              return (
                <button
                  key={tab.key || 'all'}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => selectStatusFilter(tab.key)}
                  className={`px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-all ${
                    isActive ? 'bg-surface text-ink shadow-sm' : 'text-ink-secondary hover:text-gray-900'
                  }`}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
          <button
            type="button"
            onClick={() => setShowInsights((v) => !v)}
            className="text-xs font-medium text-ink-secondary hover:text-ink underline-offset-2 hover:underline"
          >
            {showInsights ? 'Hide loss insights' : 'Why are you losing leads?'}
          </button>
        </div>

        {showInsights && (
          <div className="border-b border-border px-4 py-4 sm:px-5 bg-surface-muted/50">
            {!insights || !insights.reasons?.length ? (
              <p className="text-sm text-ink-tertiary">
                No closed-lead reasons yet. When you mark leads Not Responsive or Cancelled, reasons
                aggregate here.
              </p>
            ) : (
              <div className="space-y-2">
                <p className="text-sm font-medium text-ink">
                  Loss analysis · {insights.total_closed} closed lead
                  {insights.total_closed === 1 ? '' : 's'}
                </p>
                {insights.reasons.map((r) => (
                  <div key={r.reason} className="flex items-center gap-3 text-sm">
                    <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden">
                      <div
                        className="h-full bg-ink/80 rounded-full"
                        style={{ width: `${Math.max(r.percent, 4)}%` }}
                      />
                    </div>
                    <span className="w-28 text-ink-secondary truncate">{r.label}</span>
                    <span className="w-10 text-right font-medium text-ink">{r.percent}%</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="p-4 sm:p-5">
          {isLoading ? (
            <div className="py-20 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-ink mx-auto mb-3" />
              <p className="text-sm text-ink-tertiary">Loading leads…</p>
            </div>
          ) : consultations.length === 0 ? (
            <div className="py-16 text-center px-4">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-lg border border-border bg-surface-muted text-gray-400">
                <ClipboardList className="w-7 h-7" />
              </div>
              <h3 className="text-base font-semibold text-ink">No leads</h3>
              <p className="mt-1.5 text-sm text-ink-tertiary max-w-md mx-auto">{emptyCopy}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {consultations.map((c) => {
                const name = clientNameOf(c);
                const email = c.client?.email || c.client_email || c.client_display_email;
                const phone = c.client?.phone || c.client_phone || c.client_display_phone;
                const label = c.status_meta?.label || statuses[c.status]?.label || c.status;
                const next = c.next_action_card || {};
                const progress = c.checklist_progress;

                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => openLead(c)}
                    className={`w-full text-left product-surface-muted p-3 sm:p-4 transition-colors hover:border-ink ${
                      selectedLead?.id === c.id ? 'border-ink ring-1 ring-ink/10' : ''
                    }`}
                  >
                    <div className="flex flex-col lg:flex-row lg:items-start gap-3">
                      <div className="flex-1 min-w-0 space-y-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="text-sm sm:text-base font-semibold text-ink truncate">{name}</h3>
                          {!c.client_id && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border border-border bg-surface text-ink-secondary">
                              <User className="w-3 h-3" />
                              Guest
                            </span>
                          )}
                          <StatusBadge status={c.status} label={label} />
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-ink-secondary">
                          <span className="inline-flex items-center gap-1.5">
                            <Calendar className="w-3.5 h-3.5 text-gray-400" />
                            Requested {formatDateTime(c.created_at)}
                          </span>
                          {(c.scheduled_date || c.scheduled_time) && (
                            <span className="inline-flex items-center gap-1.5">
                              <Clock className="w-3.5 h-3.5 text-gray-400" />
                              {formatDate(c.scheduled_date)} {formatTime(c.scheduled_time)}
                            </span>
                          )}
                          {c.service?.service_name && (
                            <span className="font-medium text-ink">{c.service.service_name}</span>
                          )}
                        </div>
                        {(email || phone) && (
                          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink-tertiary">
                            {email && (
                              <span className="inline-flex items-center gap-1.5">
                                <Mail className="w-3.5 h-3.5" />
                                {email}
                              </span>
                            )}
                            {phone && (
                              <span className="inline-flex items-center gap-1.5">
                                <Phone className="w-3.5 h-3.5" />
                                {phone}
                              </span>
                            )}
                          </div>
                        )}
                        {next.title && (
                          <div className="mt-1 flex items-start gap-2 rounded-lg border border-border bg-surface px-3 py-2">
                            <ArrowRight className="w-3.5 h-3.5 mt-0.5 text-ink shrink-0" />
                            <div className="min-w-0">
                              <p className="text-xs font-semibold text-ink">Next action</p>
                              <p className="text-xs text-ink-secondary leading-snug">{next.title}</p>
                            </div>
                          </div>
                        )}
                        {progress?.total > 0 && (
                          <p className="text-[11px] text-ink-tertiary">
                            Checklist {progress.completed}/{progress.total}
                          </p>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
                        {c.status === 'pending' && (
                          <>
                            <Button
                              size="sm"
                              variant="primary"
                              className="!rounded-lg"
                              onClick={() => openLead(c)}
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Open &amp; schedule
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="!rounded-lg text-red-600 border-red-200"
                              loading={rejectMutation.isPending}
                              onClick={async () => {
                                const ok = await confirm({
                                  title: 'Reject this request?',
                                  message: 'This moves the lead to Rejected on your board.',
                                  confirmLabel: 'Reject',
                                  variant: 'danger',
                                });
                                if (!ok) return;
                                rejectMutation.mutate({ id: c.id, data: {} });
                              }}
                            >
                              <XCircle className="w-4 h-4 mr-1" />
                              Reject
                            </Button>
                          </>
                        )}
                        {(c.status === 'meeting_scheduled' || c.status === 'accepted') && (
                          <>
                            {c.google_meet_link && (
                              <a
                                href={c.google_meet_link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 text-sm font-medium text-ink hover:underline px-2"
                              >
                                <ExternalLink className="w-4 h-4" />
                                Join
                              </a>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              className="!rounded-lg"
                              loading={completeMutation.isPending}
                              onClick={async () => {
                                const ok = await confirm({
                                  title: 'Mark meeting as done?',
                                  message: 'This moves the lead to Meeting Done so you can follow up.',
                                  confirmLabel: 'Meeting done',
                                  variant: 'primary',
                                });
                                if (!ok) return;
                                completeMutation.mutate(c.id);
                              }}
                            >
                              Meeting done
                            </Button>
                          </>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          className="!rounded-lg"
                          onClick={() => openLead(c)}
                        >
                          Open
                        </Button>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
      ) : null}

      {/* Lead detail drawer / modal */}
      <ModalShell
        variant="drawer"
        isOpen={Boolean(selectedLead)}
        onClose={() => {
          // Prefer canceling the calendar-title step over closing the whole lead.
          if (pendingCalendarAdd) {
            setPendingCalendarAdd(null);
            return;
          }
          selectedLeadRef.current = null;
          setSelectedLead(null);
          if (searchParams.get('lead')) {
            const next = new URLSearchParams(searchParams);
            next.delete('lead');
            setSearchParams(next, { replace: true });
          }
        }}
        labelledBy="lead-detail-title"
        panelClassName="!max-w-lg"
      >
        {selectedLead && (
          <div className="flex h-full min-h-0 flex-col bg-surface">
            <div className="flex shrink-0 flex-wrap items-start justify-between gap-3 border-b border-border px-5 py-4 sm:px-6">
              <div>
                <h3 id="lead-detail-title" className="text-lg font-semibold text-ink">
                  {clientNameOf(selectedLead)}
                </h3>
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  <StatusBadge
                    status={selectedLead.status}
                    label={statusMeta.label || selectedLead.status}
                  />
                  <span className="text-xs text-ink-tertiary">
                    Submitted {formatDateTime(selectedLead.created_at)}
                  </span>
                </div>
              </div>
              <button
                type="button"
                aria-label="Close"
                className="rounded-md border border-border p-1.5 text-ink-tertiary hover:bg-surface-muted"
                onClick={() => {
                  selectedLeadRef.current = null;
                  setSelectedLead(null);
                  if (searchParams.get('lead')) {
                    const next = new URLSearchParams(searchParams);
                    next.delete('lead');
                    setSearchParams(next, { replace: true });
                  }
                }}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="min-h-0 flex-1 space-y-6 overflow-y-auto p-5 sm:p-6">

            {statusMeta.description && (
              <p className="text-sm text-ink-secondary leading-relaxed">{statusMeta.description}</p>
            )}

            <LeadWorkspaceLinks
              channel="internal"
              lead={{
                ...selectedLead,
                converted_booking_id: selectedLead.booking_id,
                primary_invoice_id:
                  selectedLead.primary_invoice_id || selectedLead.links?.invoice_id || null,
                primary_contract_id:
                  selectedLead.primary_contract_id || selectedLead.links?.contract_id || null,
                links: {
                  booking_id: selectedLead.booking_id,
                  invoice_id:
                    selectedLead.primary_invoice_id || selectedLead.links?.invoice_id || null,
                  contract_id:
                    selectedLead.primary_contract_id || selectedLead.links?.contract_id || null,
                  booking_href:
                    selectedLead.links?.booking_href ||
                    (selectedLead.booking_id
                      ? `/vendor/bookings/${selectedLead.booking_id}`
                      : null),
                  invoice_href:
                    selectedLead.links?.invoice_href ||
                    (selectedLead.booking_id
                      ? `/vendor/invoices/create?channel=internal&booking_id=${selectedLead.booking_id}`
                      : '/vendor/invoices/create?channel=internal'),
                  contract_href:
                    selectedLead.links?.contract_href ||
                    (selectedLead.booking_id
                      ? `/vendor/contracts/create?booking_id=${selectedLead.booking_id}`
                      : '/vendor/contracts/create'),
                },
              }}
            />

            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="!rounded-md"
                onClick={() => setShowAskAgent(true)}
              >
                <Bot className="mr-1 h-3.5 w-3.5" />
                Run agent
              </Button>
            </div>

            {/* Next action card */}
            {(selectedLead.next_action_card?.title || selectedLead.next_action) && (
              <div className="rounded-xl border border-border bg-surface-muted p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-ink-tertiary mb-1">
                  Action required
                </p>
                <p className="text-sm font-semibold text-ink">
                  {selectedLead.next_action_card?.title || selectedLead.next_action}
                </p>
                {selectedLead.next_action_card?.body && (
                  <p className="mt-1 text-sm text-ink-secondary">{selectedLead.next_action_card.body}</p>
                )}
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="primary"
                    className="!rounded-lg"
                    loading={emailMutation.isPending}
                    onClick={() => {
                      const email =
                        selectedLead.client_display_email ||
                        selectedLead.client_email ||
                        selectedLead.client?.email;
                      if (email) {
                        const subject = encodeURIComponent(
                          `Following up on your consultation — ${clientNameOf(selectedLead)}`
                        );
                        window.open(`mailto:${email}?subject=${subject}`, '_blank');
                      }
                      emailMutation.mutate(selectedLead.id);
                    }}
                  >
                    <Mail className="w-3.5 h-3.5 mr-1" />
                    Send / log email
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="!rounded-lg"
                    onClick={() => document.getElementById('call-outcome-section')?.scrollIntoView({ behavior: 'smooth' })}
                  >
                    <Phone className="w-3.5 h-3.5 mr-1" />
                    Log call
                  </Button>
                </div>
              </div>
            )}

            {/* Event date — client’s job date (flows to invoice + booking) */}
            <div className="rounded-xl border border-border p-3 space-y-2.5">
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs font-medium text-ink-tertiary uppercase">Event date</p>
                <span className="text-[11px] text-ink-tertiary">
                  Used on invoice &amp; booking
                </span>
              </div>
              {['booked', 'rejected', 'lead_cancelled'].includes(selectedLead.status) ? (
                <p className="text-sm text-ink">
                  {dealDraft.event_date || selectedLead.event_date
                    ? formatDate(dealDraft.event_date || selectedLead.event_date)
                    : 'Not set'}
                </p>
              ) : (
                <>
                  <DatePicker
                    label="Client event / job date"
                    size="sm"
                    value={dealDraft.event_date || ''}
                    onChange={(v) =>
                      setDealDraft((d) => ({
                        ...d,
                        event_date: v ? String(v).slice(0, 10) : '',
                      }))
                    }
                    allowPastDates={false}
                    placeholder="Select event date"
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    className="!rounded-lg"
                    loading={dealMutation.isPending}
                    onClick={() =>
                      dealMutation.mutate({
                        id: selectedLead.id,
                        deal: {
                          ...(selectedLead.deal_summary || {}),
                          event_date: dealDraft.event_date || null,
                        },
                      })
                    }
                  >
                    Save event date
                  </Button>
                </>
              )}
            </div>

            {/* Contact + schedule — stacked full-width rows */}
            <div className="grid grid-cols-1 gap-3 text-sm">
              <div className="rounded-xl border border-border p-3 space-y-1">
                <p className="text-xs font-medium text-ink-tertiary uppercase">Contact</p>
                <p className="text-ink">{selectedLead.client_display_email || selectedLead.client_email || '—'}</p>
                <p className="text-ink-secondary">
                  {selectedLead.client_display_phone || selectedLead.client_phone || 'No phone'}
                </p>
              </div>
              <div className="rounded-xl border border-border p-3 space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs font-medium text-ink-tertiary uppercase">Meeting</p>
                  {!['booked', 'rejected', 'lead_cancelled', 'lead_not_responsive'].includes(
                    selectedLead.status
                  ) && (
                    <span className="text-[11px] text-ink-tertiary">Editable</span>
                  )}
                </div>

                {['booked', 'rejected', 'lead_cancelled', 'lead_not_responsive'].includes(
                  selectedLead.status
                ) ? (
                  <>
                    <p className="text-ink">
                      {formatDate(selectedLead.scheduled_date)} ·{' '}
                      {formatTime(selectedLead.scheduled_time)}
                    </p>
                    {selectedLead.google_meet_link ? (
                      <a
                        href={selectedLead.google_meet_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-ink hover:underline text-xs font-medium"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                        Meeting link
                      </a>
                    ) : (
                      <p className="text-ink-tertiary text-xs">No meeting link yet</p>
                    )}
                  </>
                ) : (
                  <div className="space-y-2.5">
                    <DatePicker
                      label="Date"
                      size="sm"
                      value={scheduleDate}
                      onChange={(v) => setScheduleDate(v || '')}
                      allowPastDates={false}
                      placeholder="Select date"
                    />
                    <TimePicker
                      label="Time"
                      size="sm"
                      value={scheduleTime}
                      onChange={(v) => setScheduleTime(v || '')}
                      format="12"
                      placeholder="Select time"
                    />
                    <Input
                      label="Meeting link"
                      type="url"
                      value={scheduleLink}
                      onChange={(e) => setScheduleLink(e.target.value)}
                      placeholder="https://meet.google.com/…"
                    />
                    {selectedLead.action_checklist?.added_to_calendar && (
                      <Input
                        label="Calendar title"
                        value={calendarTitleDraft || selectedLead.calendar_title || ''}
                        onChange={(e) => setCalendarTitleDraft(e.target.value)}
                        placeholder="Title shown on your calendar"
                        maxLength={180}
                      />
                    )}
                    <Button
                      size="sm"
                      variant="primary"
                      className="!rounded-lg w-full"
                      loading={scheduleMutation.isPending || checklistMutation.isPending}
                      disabled={
                        !scheduleDate ||
                        (scheduleDate === toDateInputValue(selectedLead.scheduled_date) &&
                          scheduleTime === toTimeInputValue(selectedLead.scheduled_time) &&
                          (scheduleLink || '') === (selectedLead.google_meet_link || '') &&
                          !(
                            selectedLead.action_checklist?.added_to_calendar &&
                            calendarTitleDraft.trim() &&
                            calendarTitleDraft.trim() !== (selectedLead.calendar_title || '')
                          ))
                      }
                      onClick={() => {
                        if (!scheduleDate) {
                          showError(null, 'Choose a consultation date');
                          return;
                        }
                        const link = scheduleLink.trim();
                        if (link && !/^https?:\/\//i.test(link)) {
                          showError(null, 'Meeting link must start with https://');
                          return;
                        }
                        const titleChanged =
                          selectedLead.action_checklist?.added_to_calendar &&
                          calendarTitleDraft.trim() &&
                          calendarTitleDraft.trim() !== (selectedLead.calendar_title || '');
                        const scheduleChanged =
                          scheduleDate !== toDateInputValue(selectedLead.scheduled_date) ||
                          scheduleTime !== toTimeInputValue(selectedLead.scheduled_time) ||
                          (scheduleLink || '') !== (selectedLead.google_meet_link || '');

                        if (scheduleChanged) {
                          scheduleMutation.mutate({
                            id: selectedLead.id,
                            data: {
                              scheduled_date: scheduleDate,
                              scheduled_time: scheduleTime || null,
                              google_meet_link: link || null,
                            },
                          });
                        }
                        if (titleChanged) {
                          const { current } = buildChecklistMap(selectedLead, null, null);
                          applyChecklistMutation(
                            selectedLead,
                            'added_to_calendar',
                            true,
                            current,
                            { calendar_title: calendarTitleDraft.trim() }
                          );
                        }
                      }}
                    >
                      Save schedule
                    </Button>
                    <div className="flex flex-wrap items-center gap-2">
                      {meetingLinkAlreadySent(selectedLead) ? (
                        <span className="inline-flex items-center rounded-md bg-emerald-50 px-2 py-1 text-[11px] font-medium text-emerald-800 border border-emerald-200">
                          Link emailed
                        </span>
                      ) : null}
                      {(scheduleLink || selectedLead.google_meet_link) && (
                        <>
                          {!meetingLinkAlreadySent(selectedLead) &&
                          selectedLead.status === 'pending' ? (
                            <Button
                              size="sm"
                              variant="outline"
                              className="!rounded-lg"
                              loading={acceptMutation.isPending}
                              onClick={() => {
                                const link = (scheduleLink || selectedLead.google_meet_link || '').trim();
                                if (!link) {
                                  showError(null, 'Add a meeting link first');
                                  return;
                                }
                                if (!/^https?:\/\//i.test(link)) {
                                  showError(null, 'Meeting link must start with https://');
                                  return;
                                }
                                setMeetingLink(link);
                                acceptMutation.mutate({
                                  id: selectedLead.id,
                                  data: {
                                    google_meet_link: link,
                                    vendor_notes: null,
                                  },
                                });
                              }}
                            >
                              <Mail className="w-3.5 h-3.5 mr-1" />
                              Email link once
                            </Button>
                          ) : null}
                          {!meetingLinkAlreadySent(selectedLead) &&
                          (selectedLead.status === 'meeting_scheduled' ||
                            selectedLead.status === 'accepted') ? (
                            <Button
                              size="sm"
                              variant="outline"
                              className="!rounded-lg"
                              loading={checklistMutation.isPending}
                              onClick={() => {
                                const link = (scheduleLink || selectedLead.google_meet_link || '').trim();
                                if (!link) {
                                  showError(null, 'Add a meeting link first');
                                  return;
                                }
                                const { current } = buildChecklistMap(
                                  selectedLead,
                                  'meeting_link_sent',
                                  true
                                );
                                applyChecklistMutation(
                                  selectedLead,
                                  'meeting_link_sent',
                                  true,
                                  current
                                );
                              }}
                            >
                              <Mail className="w-3.5 h-3.5 mr-1" />
                              Email link once
                            </Button>
                          ) : null}
                          <button
                            type="button"
                            className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1.5 text-xs font-medium text-ink hover:bg-surface-muted"
                            onClick={async () => {
                              const link = (scheduleLink || selectedLead.google_meet_link || '').trim();
                              try {
                                await navigator.clipboard.writeText(link);
                                success(
                                  null,
                                  meetingLinkAlreadySent(selectedLead)
                                    ? 'Copied — share from your own email'
                                    : 'Link copied'
                                );
                              } catch {
                                showError(null, 'Could not copy link');
                              }
                            }}
                          >
                            <Copy className="h-3.5 w-3.5" />
                            Copy
                          </button>
                          <a
                            href={scheduleLink || selectedLead.google_meet_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1.5 text-xs font-medium text-ink hover:bg-surface-muted"
                          >
                            <ExternalLink className="h-3.5 w-3.5" />
                            Open
                          </a>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Questionnaire — custom answers only (contact lives above) */}
            {(() => {
              const rawRows = selectedLead.questionnaire_labeled?.length
                ? selectedLead.questionnaire_labeled
                : Object.entries(selectedLead.questionnaire_responses || {}).map(
                    ([key, value]) => ({ key, label: key, value })
                  );
              const rows = filterClientRequestRows(rawRows, selectedLead);
              if (!rows.length) return null;
              return (
                <div>
                  <p className="text-sm font-semibold text-ink mb-2">Client request</p>
                  <ul className="space-y-1.5 text-sm text-ink-secondary rounded-xl border border-border p-3">
                    {rows.map((row) => (
                      <li key={row.key}>
                        <span className="font-medium text-ink">{row.label}:</span>{' '}
                        {String(row.value)}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })()}

            {/* Action plan (short) */}
            {actionPlan.length > 0 && (
              <div>
                <p className="text-sm font-semibold text-ink mb-2 flex items-center gap-2">
                  <ClipboardList className="w-4 h-4" />
                  What to do
                </p>
                <ol className="space-y-1.5 text-sm text-ink-secondary list-decimal list-inside">
                  {actionPlan.map((step) => (
                    <li key={step} className="leading-snug">
                      {step}
                    </li>
                  ))}
                </ol>
              </div>
            )}

            {/* Checklist — one clear step per stage */}
            {checklistItems.length > 0 && (
              <div>
                <p className="text-sm font-semibold text-ink mb-1 flex items-center gap-2">
                  <CheckSquare className="w-4 h-4" />
                  Next step
                </p>
                <p className="text-[11px] text-ink-tertiary mb-2">
                  Short stage checklist — meeting link / contact actions auto-help where possible.
                </p>

                {pendingCalendarAdd && (
                  <div className="mb-3 rounded-xl border border-orange-200 bg-orange-50 p-3 space-y-2.5">
                    <p className="text-sm font-semibold text-orange-950">
                      Add to calendar
                    </p>
                    <p className="text-xs text-orange-900/80">
                      Enter a title for this lead on your calendar, then confirm.
                    </p>
                    <Input
                      label="Calendar title"
                      value={calendarTitleDraft}
                      onChange={(e) => setCalendarTitleDraft(e.target.value)}
                      placeholder="e.g. Consultation — Samuels wedding"
                      maxLength={180}
                      autoFocus
                    />
                    <div className="flex flex-wrap gap-2 justify-end">
                      <Button
                        size="sm"
                        variant="outline"
                        className="!rounded-lg"
                        onClick={() => setPendingCalendarAdd(null)}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        variant="primary"
                        className="!rounded-lg"
                        loading={checklistMutation.isPending}
                        onClick={confirmAddToCalendar}
                      >
                        Add to calendar
                      </Button>
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  {checklistItems.map((item) => (
                    <button
                      key={item.key}
                      type="button"
                      disabled={checklistMutation.isPending || Boolean(pendingCalendarAdd)}
                      onClick={() => toggleChecklistItem(item.key, item.done)}
                      className="w-full flex items-center gap-2.5 text-left text-sm rounded-lg border border-border px-3 py-2 hover:bg-surface-muted disabled:opacity-60 disabled:pointer-events-none"
                    >
                      {item.done ||
                      (pendingCalendarAdd && item.key === 'added_to_calendar') ? (
                        <CheckSquare className="w-4 h-4 text-emerald-600 shrink-0" />
                      ) : (
                        <Square className="w-4 h-4 text-gray-400 shrink-0" />
                      )}
                      <span
                        className={
                          item.done ||
                          (pendingCalendarAdd && item.key === 'added_to_calendar')
                            ? 'text-ink-tertiary line-through'
                            : 'text-ink'
                        }
                      >
                        {item.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Deal summary (meeting_done) */}
            {(selectedLead.status === 'meeting_done' || selectedLead.status === 'booked') && (
              <div>
                <p className="text-sm font-semibold text-ink mb-2 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Agreed deal
                </p>
                <div className="space-y-3">
                  <DatePicker
                    label="Event date"
                    size="sm"
                    value={dealDraft.event_date || ''}
                    onChange={(v) =>
                      setDealDraft((d) => ({
                        ...d,
                        event_date: v ? String(v).slice(0, 10) : '',
                      }))
                    }
                    allowPastDates={false}
                    placeholder="Select event date"
                  />
                  {DEAL_FIELDS.map((f) => (
                    <Input
                      key={f.key}
                      label={f.label}
                      value={dealDraft[f.key] || ''}
                      onChange={(e) =>
                        setDealDraft((d) => ({ ...d, [f.key]: e.target.value }))
                      }
                      placeholder={f.label}
                    />
                  ))}
                  <Button
                    size="sm"
                    variant="primary"
                    className="!rounded-lg"
                    loading={dealMutation.isPending}
                    onClick={() =>
                      dealMutation.mutate({
                        id: selectedLead.id,
                        deal: {
                          ...dealDraft,
                          event_date: dealDraft.event_date || null,
                        },
                      })
                    }
                  >
                    Save deal summary
                  </Button>
                </div>
              </div>
            )}

            {/* Change status */}
            <div>
              <CustomSelect
                label="Change status"
                value={statusDraft}
                onChange={(v) => {
                  setStatusDraft(v);
                  setCloseReason('');
                }}
                options={[
                  {
                    value: selectedLead.status,
                    label: `${statusMeta.label || selectedLead.status} (current)`,
                  },
                  ...(statusMeta.allowed_next || []).map((key) => ({
                    value: key,
                    label: statuses[key]?.label || key,
                  })),
                ]}
                placeholder="Select status…"
              />

              {needsCloseReason && (
                <div className="mt-3 space-y-3">
                  <CustomSelect
                    label={
                      statusDraft === 'lead_not_responsive'
                        ? 'Why do you believe this lead is no longer responsive?'
                        : 'Why did the client decide not to proceed?'
                    }
                    value={closeReason}
                    onChange={setCloseReason}
                    options={(draftStatusMeta.close_reasons || []).map((r) => ({
                      value: r,
                      label: draftStatusMeta.close_reason_labels?.[r] || r,
                    }))}
                    placeholder="Select a reason…"
                  />
                  <Input
                    label="Additional notes"
                    value={closeNotes}
                    onChange={(e) => setCloseNotes(e.target.value)}
                    placeholder="Short explanation"
                  />
                </div>
              )}

              <div className="mt-3 flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="primary"
                  className="!rounded-lg"
                  disabled={statusDraft === selectedLead.status || statusMutation.isPending}
                  loading={statusMutation.isPending}
                  onClick={handleStatusChange}
                >
                  Update status
                </Button>
                {(selectedLead.status === 'meeting_scheduled' ||
                  selectedLead.status === 'accepted') && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="!rounded-lg"
                    loading={completeMutation.isPending}
                    onClick={() => completeMutation.mutate(selectedLead.id)}
                  >
                    Mark meeting done
                  </Button>
                )}
              </div>
            </div>

            {/* Call outcome */}
            <div id="call-outcome-section" className="space-y-2">
              <p className="text-sm font-semibold text-ink flex items-center gap-2">
                <Phone className="w-4 h-4" />
                Log call outcome
              </p>
              <CustomSelect
                value={callOutcome}
                onChange={setCallOutcome}
                options={Object.entries(callOutcomes).map(([k, label]) => ({
                  value: k,
                  label,
                }))}
                placeholder="Select outcome…"
              />
              <Input
                label="Call notes"
                value={callNotes}
                onChange={(e) => setCallNotes(e.target.value)}
                placeholder="What did you discuss?"
              />
              <Button
                size="sm"
                variant="outline"
                className="!rounded-lg mt-2"
                disabled={!callOutcome}
                loading={callMutation.isPending}
                onClick={() =>
                  callMutation.mutate({
                    id: selectedLead.id,
                    data: { outcome: callOutcome, notes: callNotes || null },
                  })
                }
              >
                Save call
              </Button>
            </div>

            {/* Tags (Automations If filter) */}
            <div>
              <Input
                label="Tags"
                value={dealDraft.tags || ''}
                onChange={(e) =>
                  setDealDraft((d) => ({ ...d, tags: e.target.value }))
                }
                placeholder="e.g. vip, wedding, rush"
              />
              <p className="mt-1 text-[11px] text-ink-tertiary">
                Used by Automations “If tag” filters. Comma-separated.
              </p>
              <Button
                size="sm"
                variant="outline"
                className="!rounded-lg mt-2"
                loading={dealMutation.isPending}
                onClick={() =>
                  dealMutation.mutate({
                    id: selectedLead.id,
                    deal: {
                      ...(selectedLead.deal_summary || {}),
                      tags: dealDraft.tags || '',
                    },
                  })
                }
              >
                Save tags
              </Button>
            </div>

            {/* Notes */}
            <div>
              <p className="text-sm font-semibold text-ink mb-2 flex items-center gap-2">
                <MessageSquare className="w-4 h-4" />
                Add note
              </p>
              <textarea
                value={noteDraft}
                onChange={(e) => setNoteDraft(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-border rounded-xl bg-surface text-ink text-sm focus:outline-none focus:ring-2 focus:ring-black"
                placeholder="Internal note about this lead…"
              />
              <Button
                size="sm"
                variant="outline"
                className="!rounded-lg mt-2"
                disabled={!noteDraft.trim()}
                loading={noteMutation.isPending}
                onClick={() =>
                  noteMutation.mutate({ id: selectedLead.id, note: noteDraft.trim() })
                }
              >
                Save note
              </Button>
              {selectedLead.vendor_notes && (
                <pre className="mt-3 whitespace-pre-wrap text-xs text-ink-secondary rounded-xl border border-border bg-surface-muted p-3 font-sans">
                  {selectedLead.vendor_notes}
                </pre>
              )}
            </div>

            {/* Activity timeline */}
            {Array.isArray(selectedLead.activities) && selectedLead.activities.length > 0 && (
              <div>
                <p className="text-sm font-semibold text-ink mb-2">Activity</p>
                <ul className="space-y-2 max-h-48 overflow-y-auto">
                  {selectedLead.activities.map((a) => (
                    <li
                      key={a.id}
                      className="text-xs border-l-2 border-border pl-3 py-0.5"
                    >
                      <span className="font-medium text-ink">{a.title}</span>
                      {a.body && <span className="text-ink-secondary"> — {a.body}</span>}
                      <div className="text-ink-tertiary">{formatDateTime(a.created_at)}</div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Meta */}
            <div className="grid grid-cols-2 gap-2 text-[11px] text-ink-tertiary border-t border-border pt-3">
              <p>Last contact: {formatDateTime(selectedLead.last_contact_at)}</p>
              <p>Last email: {formatDateTime(selectedLead.last_email_sent_at)}</p>
              <p>Last call: {formatDateTime(selectedLead.last_call_at)}</p>
              <p>Consultation done: {formatDateTime(selectedLead.consultation_completed_at)}</p>
            </div>

            {selectedLead.status === 'booked' || selectedLead.booking_id ? (
              <div className="border-t border-border pt-4">
                <p className="rounded-md border border-border bg-surface-muted px-3 py-2 text-[11px] text-ink-tertiary">
                  Booked leads can’t be deleted. Open the linked booking, invoice, or contract from
                  Connected work above.
                </p>
              </div>
            ) : (
              <div className="border-t border-border pt-4">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  icon={Trash2}
                  className="!w-full !rounded-lg !border-red-200 !text-red-700 hover:!bg-red-50"
                  loading={deleteMutation.isPending}
                  onClick={async () => {
                    const ok = await confirm({
                      title: 'Delete this lead?',
                      message: 'Linked bookings, invoices, and contracts are kept.',
                      confirmLabel: 'Delete lead',
                      variant: 'danger',
                    });
                    if (!ok) return;
                    deleteMutation.mutate(selectedLead.id);
                  }}
                >
                  Delete lead
                </Button>
              </div>
            )}
            </div>
          </div>
        )}
      </ModalShell>

      {/* Schedule meeting modal (legacy quick path) */}
      <ModalShell
        isOpen={Boolean(showAcceptModal)}
        onClose={closeAcceptModal}
        labelledBy="accept-consultation-title"
        panelClassName="max-w-md overflow-hidden rounded-xl border border-border bg-surface shadow-xl"
      >
        <div className="bg-surface">
          <div className="flex items-start justify-between gap-3 border-b border-border px-5 py-3.5 sm:px-6">
            <h3 id="accept-consultation-title" className="text-lg font-semibold text-ink">
              {acceptedMeetingLink ? 'Meeting scheduled' : 'Schedule consultation meeting'}
            </h3>
            <button
              type="button"
              aria-label="Close"
              onClick={closeAcceptModal}
              className="rounded-md border border-border p-1.5 text-ink-tertiary hover:bg-surface-muted"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="p-5 sm:p-6">
            {acceptedMeetingLink ? (
              <>
                <div className="flex items-center gap-2.5 mb-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-600">
                    <CheckCircle className="w-5 h-5" />
                  </div>
                  <p className="text-sm text-ink-secondary">
                    Link was emailed once. To share again, copy it and send from your own email.
                  </p>
                </div>
                <div className="flex items-center gap-2 p-3 bg-surface-muted rounded-xl border border-border mb-5">
                  <a
                    href={acceptedMeetingLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 text-sm text-ink hover:underline truncate"
                  >
                    {acceptedMeetingLink}
                  </a>
                  <Button
                    size="sm"
                    variant="outline"
                    className="!rounded-lg"
                    onClick={() => {
                      navigator.clipboard.writeText(acceptedMeetingLink);
                      success(null, 'Link copied');
                    }}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <Button variant="primary" className="w-full rounded-md" onClick={closeAcceptModal}>
                  Done
                </Button>
              </>
            ) : (
              <>
                <p className="text-sm text-ink-secondary mb-4">
                  Add your video call link. This moves the lead to Meeting Scheduled and emails the
                  client once.
                </p>
                <div className="space-y-4">
                  <Input
                    label="Meeting link"
                    type="url"
                    placeholder="https://zoom.us/j/... or meet.google.com/..."
                    value={meetingLink}
                    onChange={(e) => {
                      setMeetingLink(e.target.value);
                      if (meetingLinkError) setMeetingLinkError('');
                    }}
                    error={meetingLinkError}
                  />
                  <Input
                    label="Notes (optional)"
                    placeholder="Any notes for the client"
                    value={vendorNotes}
                    onChange={(e) => setVendorNotes(e.target.value)}
                  />
                </div>
                <div className="flex gap-2 mt-6 justify-end">
                  <Button variant="outline" className="rounded-md" onClick={closeAcceptModal}>
                    Cancel
                  </Button>
                  <Button
                    className="rounded-md"
                    onClick={() => handleAccept(showAcceptModal)}
                    loading={acceptMutation.isPending}
                    disabled={!meetingLink.trim() || acceptMutation.isPending}
                  >
                    Schedule &amp; email once
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </ModalShell>

      <AskAgentModal
        isOpen={showAskAgent}
        onClose={() => setShowAskAgent(false)}
        canAgents={canAgents}
        presetEntity={
          selectedLead
            ? {
                type: 'consultation',
                id: String(selectedLead.id),
                label: clientNameOf(selectedLead),
              }
            : null
        }
      />

    </div>
  );
};

export default VendorConsultations;
